/**
 * SBN (Strength by Numbers) Settings — GraphQL endpoint + API key.
 *
 * The plaintext key is never fetched back once saved. The UI shows only the
 * last 4 characters of the stored key so the operator can confirm which
 * key is active. A show/hide toggle reveals what you're typing in the
 * input during a change; the stored key itself is never exposed.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Activity, Save, Trash2, PlugZap, CheckCircle2, AlertCircle, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function SbnSettings() {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  async function refresh() {
    try {
      const r = await api.get("/settings/sbn");
      setCfg(r.data);
      setEndpoint(r.data.endpoint || "");
    } catch (e) {
      toast.error("Failed to load SbN settings", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }
  useEffect(() => { refresh(); }, []);

  async function save() {
    setSaving(true);
    setTestResult(null);
    try {
      const body = { endpoint };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const r = await api.put("/settings/sbn", body);
      setCfg(r.data);
      setApiKey("");
      setShowKey(false);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    setTestResult(null);
    try {
      const body = {};
      if (apiKey.trim()) body.api_key = apiKey.trim();
      if (endpoint.trim()) body.endpoint = endpoint.trim();
      const r = await api.post("/settings/sbn/test-connection", body);
      setTestResult(r.data);
      if (r.data.ok) {
        const msg =
          r.data.total_count != null
            ? `Connected · ${r.data.total_count} clients visible`
            : "Connected";
        toast.success(msg);
      } else {
        toast.error("Test failed", { description: r.data.error });
      }
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || e.message });
      toast.error("Test failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setTesting(false);
    }
  }

  async function clearKey() {
    if (!window.confirm(
      "Remove the stored SbN API key? The SOAP notes assistant will no longer be able to pull dynamometry results until you save a new key.",
    )) return;
    setDeleting(true);
    setTestResult(null);
    try {
      const r = await api.delete("/settings/sbn");
      setCfg(r.data);
      setApiKey("");
      setShowKey(false);
      toast.success("Key removed");
    } catch (e) {
      toast.error("Delete failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setDeleting(false);
    }
  }

  const configured = cfg?.has_key || cfg?.env_fallback_key;

  return (
    <Card
      className="p-6 rounded-lg border border-zinc-200 shadow-none"
      data-testid="sbn-settings"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <Activity className="h-3.5 w-3.5" /> Strength by Numbers · assessment data
          </div>
          <h3 className="mt-1 font-display text-xl tracking-tight text-zinc-950">
            SbN GraphQL credentials
          </h3>
          <p className="mt-1 text-xs text-zinc-600 max-w-xl leading-relaxed">
            Used by the SOAP notes assistant to pull the latest dynamometry
            and force-plate results from your assessment tablet. Keys are
            stored encrypted at rest and never returned to the browser once
            saved. Get your key from your Strength by Numbers / Axit Connect
            admin console.
          </p>
        </div>
        {configured ? (
          <span
            className="shrink-0 inline-flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 rounded"
            data-testid="sbn-status-badge"
          >
            <CheckCircle2 className="h-3 w-3" /> Configured
          </span>
        ) : (
          <span
            className="shrink-0 inline-flex items-center gap-1 text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200 px-2 py-1 rounded"
            data-testid="sbn-status-badge"
          >
            <AlertCircle className="h-3 w-3" /> Not configured
          </span>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            GraphQL endpoint
          </Label>
          <Input
            type="text"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder={cfg?.default_endpoint || "https://server.connect.strengthbynumbers.com/graphql"}
            className="border-zinc-300 mt-1.5 font-mono-num text-xs"
            data-testid="sbn-endpoint-input"
            autoComplete="off"
          />
          <p className="mt-1 text-2xs text-zinc-500">
            Default: <code>{cfg?.default_endpoint || "https://server.connect.strengthbynumbers.com/graphql"}</code>
          </p>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            API key
          </Label>
          <div className="mt-1.5 relative">
            <Input
              type={showKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                cfg?.has_key
                  ? `Saved · ••••${cfg.key_last4 ?? "····"} · leave blank to keep`
                  : cfg?.env_fallback_key
                    ? "Using SBN_API_KEY from .env · type to override"
                    : "Paste your SbN API key"
              }
              className="border-zinc-300 font-mono-num text-xs pr-10"
              data-testid="sbn-api-key-input"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              data-testid="sbn-toggle-key-visibility"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900"
              aria-label="Toggle SbN API key visibility"
              disabled={!apiKey}
              title={apiKey ? "Show/hide the key you're typing" : "Type a new key to reveal"}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {testResult && (
        <div
          data-testid="sbn-test-result"
          className={cn(
            "mt-4 p-3 rounded-md border text-sm",
            testResult.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-700",
          )}
        >
          {testResult.ok ? (
            <span>
              Connection OK
              {testResult.total_count != null && (
                <> · {testResult.total_count} clients visible</>
              )}
              {testResult.endpoint && (
                <> · <code className="text-2xs">{testResult.endpoint}</code></>
              )}
            </span>
          ) : (
            <span>Test failed · {testResult.error}</span>
          )}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2 border-t border-zinc-100 pt-5">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="sbn-save-btn"
        >
          <Save className={cn("h-4 w-4 mr-1.5", saving && "animate-pulse")} />
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          variant="outline"
          onClick={test}
          disabled={testing || (!configured && !apiKey.trim())}
          className="border-zinc-300 hover:bg-zinc-100"
          data-testid="sbn-test-btn"
          title={
            !configured && !apiKey.trim()
              ? "Enter a key or save one first"
              : "Run a lightweight GraphQL ping"
          }
        >
          <PlugZap className={cn("h-4 w-4 mr-1.5", testing && "animate-pulse")} />
          {testing ? "Testing…" : "Test connection"}
        </Button>
        {cfg?.has_key && (
          <Button
            variant="outline"
            onClick={clearKey}
            disabled={deleting}
            className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 ml-auto"
            data-testid="sbn-delete-btn"
          >
            <Trash2 className={cn("h-4 w-4 mr-1.5", deleting && "animate-pulse")} />
            {deleting ? "Removing…" : "Remove key"}
          </Button>
        )}
      </div>

      {cfg?.updated_at && (
        <p className="mt-3 text-2xs text-zinc-400 font-mono-num">
          Last saved {cfg.updated_at.slice(0, 16).replace("T", " ")}
        </p>
      )}
    </Card>
  );
}
